import { useQuery, useQueryClient } from '@tanstack/react-query';
import { endpoints } from '@rossum/api-client';
import { Run, Runs } from '@rossum/api-client/hooks';
import { api } from '../../../../../lib/apiClient';
import { isRunningJob, mergeRunUpdates } from '../helpers';
import { QUERY_KEY_RUNS } from './useRuns';

export const QUERY_KEY_RUNS_STATUS_POLL = 'hooks-runs-status-poll';

type UseRunsStatusPollingParams = {
  runs: Run[];
  enabled: boolean;
};

export const useRunsStatusPolling = ({
  runs,
  enabled,
}: UseRunsStatusPollingParams) => {
  const queryClient = useQueryClient();

  const runningRuns = runs.filter(run => isRunningJob(run.status));

  // Extract UUIDs of runs that need status updates
  const runningUuids = runningRuns
    .map(run => run.uuid)
    .filter((uuid): uuid is string => uuid !== undefined); // TODO: Change type to require uuid and remove this line

  const hasRunningRuns = runningUuids.length > 0;

  // Check for runs with cancelRequested (need faster polling)
  const hasCancellingRuns = runningRuns.some(run => run.cancelRequested);

  // Determine poll interval based on state
  const getRefetchInterval = (): number | false => {
    if (!enabled || runningUuids.length === 0) return false;
    if (hasCancellingRuns) return 3000; // Fast polling for cancelling
    if (hasRunningRuns) return 10000; // Standard polling for running
    return false;
  };

  return useQuery({
    queryKey: [QUERY_KEY_RUNS_STATUS_POLL, runningUuids],
    queryFn: () =>
      api.request(
        endpoints.hooks.runs({
          uuid: runningUuids,
        })
      ),
    enabled: enabled && runningUuids.length > 0,
    refetchInterval: getRefetchInterval(),
    cacheTime: 0,
    onSuccess: (data: Runs) => {
      // Merge updated runs into main query cache
      queryClient.setQueriesData<Runs | undefined>(
        { queryKey: [QUERY_KEY_RUNS] },
        prev => {
          if (!prev) return prev;

          return {
            ...prev,
            results: mergeRunUpdates(prev.results, data.results),
          };
        }
      );
    },
  });
};
